import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components/macro';
import { withMemo } from '@mirinae/lessons/modules/libs/reactTools';

import { UserAgent } from '@mirinae/js-utils';

import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import { FormattedKoEnPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';
import { getElementComponent } from '@mirinae/lessons/components/lesson/elementComponentMap';
import PatternAnimation from '@mirinae/lessons/components/lesson/elements/PatternAnimation';
import PageElements from '@mirinae/lessons/components/lesson/PageElements';
import StepperButton from '@mirinae/lessons/components/buttons/StepperButton';
import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';
import { PageTop } from '@mirinae/lessons/components/lesson/pages/DefaultPage';

const isMobile = !UserAgent.os.isOthers;

const ExampleButtonsBox = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: ${props => props.height};
    flex-direction: ${isMobile ? 'column' : 'row'};
    margin: 20px 0px;
`;

const StartIcon = styled(StepperButton)`
    position: absolute;
    ${isMobile ? `
        right: 15px;
        top: calc(50% - 20px);
    ` : `
        bottom: 15px;
        left: calc(50% - 21px);
    `}
`;

const ExampleButton = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    min-height: 100px;
    cursor: pointer;
    background: ${lessonsPalette.primary.white};
    box-shadow: 4px 2px 20px rgba(159, 188, 186, 0.2);
    border-radius: 20px;

    :hover {
        background-color: ${lessonsPalette.grey[7]};
    }
  
    ${isMobile ? `
        width: calc(100% - 40px);
        padding: 10px 60px 10px 20px;
        justify-content: start;
        align-items: center;
        & + & {
            margin: 20px 0 0 0;
        }
    ` : `
        width: 185px;
        height: 230px;
        padding: 50px 12px;
        & + & {
            margin: 0 0 0 25px;
        }
    
        .phrase-column {
            height: 65px;
            justify-content: center;
        }
    `}
`;

const exampleStyle = {
    koFontWeight: '700', koreanFontSize: '32px',
    enFontWeight: '400', englishFontSize: '16px',
    align: isMobile ? 'left' : 'center',
    adaptiveFontScale: 0.85,
};

// custom page renderer for pattern-application and other process examples that take over the whole page
//  and handle to sequencing and layout of the process-animation components (like verbConjugation) that
//  are found in the page
const ProcessExamples = ({ page }) => {
    const [examples, setExamples] = useState([]);
    const [example, setExample] = useState(null);
    const player = useContext(PlayerContext);
    let startEx = new URLSearchParams(window.location.search).get('ex');
    startEx = startEx !== null ? Number.parseInt(startEx, 10) : null;

    useEffect(() => {
        // hunt for process components inside the page, they must have a field name 'example'
        const examples = [];
        page.children.forEach(step => step.children.filter(e => getElementComponent(e) === PatternAnimation).forEach(e => examples.push(e)));
        if (examples.length > 0) {
            setExamples(examples);
            page.hasExampleAnimations = true;
            if (startEx !== null && examples.find(e => e.index === startEx))
                setExample(examples.find(e => e.index === startEx));
        }
    }, [page]);

    const selectExample = (e) => {
        setExample(e);
        player.redrawNavButtons();
        window.history.replaceState(null, '', player.makePageURL('.', '.', e ? [`ex=${e.index}`] : []));
    };

    // render example buttons set, pick up button stack box height from the animationStageBox element passed by the component-mapper
    const ExamplesButtons = ({ element }) => {
        return (
            <ExampleButtonsBox height={isMobile ? 'auto' : `${element.geom.height}px`}>
                { !example && examples.map((e, i) => {
                    const [hover, setHover] = useState(false);
                    return (
                        <ExampleButton
                            key={e.index} last={i === examples.length - 1}
                            selected={e === example} element={e}
                            onClick={() => selectExample(e)} hover={hover}
                            onMouseOver={() => setHover(true)} onMouseOut={() => setHover(false)}
                        >
                            <FormattedKoEnPhrase
                                phrase={(e.fieldsByName.example || e.fieldsByName.Example)}
                                phraseStyle={{ ...exampleStyle, deselected: example && e !== example, noTextTools: true }}
                            />
                            <StartIcon hover={hover} icon={player.exerciseComplete(page, e) ? 'replay' : 'next'} state={!player.exerciseComplete(page, e) && 'active'} />
                        </ExampleButton>
                    );
                })}
            </ExampleButtonsBox>
        );
    };

    // render or hide PatternAnimation
    const Animation = ({ element }) => example && (
        <PatternAnimation element={example} geomElement={element} example next={() => selectExample(null)} back={() => selectExample(null)} />
    );
    const Hide = ({ element }) => {
        const ElementComponent = getElementComponent(element);
        return <div style={{ visibility: 'hidden' }}><ElementComponent element={element} /></div>;
    };

    // select elements that the default PageElements component should layout & render
    const displayFilter = (e) => getElementComponent(e) !== PatternAnimation && e.name !== 'examplesButtonBox';
    // const displayFilter = (e) => (getElementComponent(e) !== PatternAnimation && getElementComponent(e) !== MissingElement) || e.name === 'animationStageBox';

    // provide content components for each of the layout boxes in this page, hide buttons & text when showing animation; buttons use animationStageBox layoutbox(??)
    const mapComponent = (e) => {
        return example ? (e.name === 'animationStageBox' ? Animation : e.name !== 'listButton' && Hide) : e.name === 'animationStageBox' && ExamplesButtons;
    };

    // map over page elements for current page-build step
    return (
        <>
            <PageTop page={page} />
            <PageElements page={page} displayFilter={displayFilter} mapComponent={mapComponent} />
        </>
    );
};

export default withMemo(ProcessExamples, ['page']);
